import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Users, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PengurusManager = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: '', jabatan: '', foto_url: '', urutan: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pengurus`, {
        withCredentials: true,
        params: { mosque_id: user?.mosque_id }
      });
      setList(data);
    } catch {
      toast.error('Gagal memuat data pengurus');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ nama: '', jabatan: '', foto_url: '', urutan: String(list.length + 1) });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ nama: p.nama, jabatan: p.jabatan, foto_url: p.foto_url || '', urutan: String(p.urutan) });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama.trim() || !form.jabatan.trim()) return toast.error('Nama dan jabatan wajib diisi');
    setSaving(true);
    try {
      const payload = { ...form, urutan: parseInt(form.urutan) || 99 };
      if (editing) {
        await axios.put(`${API_URL}/api/pengurus/${editing.id}`, payload, { withCredentials: true });
        toast.success('Pengurus diperbarui');
      } else {
        await axios.post(`${API_URL}/api/pengurus`, payload, { withCredentials: true });
        toast.success('Pengurus ditambahkan');
      }
      closeModal();
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pengurus ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/pengurus/${id}`, { withCredentials: true });
      toast.success('Pengurus dihapus');
      fetchList();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Susunan Pengurus</h2>
          <p className="text-sm font-body text-text-secondary mt-1">
            Kelola daftar pengurus & takmir masjid
          </p>
        </div>
        <button
          data-testid="add-pengurus-btn"
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" /> Tambah Pengurus
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <p className="text-text-secondary font-body mb-4">Belum ada data pengurus</p>
          <button onClick={openAdd} className="text-emerald-700 font-body font-medium hover:underline">
            Tambah pengurus pertama
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-4"
              data-testid={`pengurus-item-${p.id}`}
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-emerald-50 flex items-center justify-center flex-shrink-0 border border-stone-100">
                {p.foto_url ? (
                  <img src={p.foto_url} alt={p.nama} className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-8 h-8 text-emerald-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body font-semibold text-text-primary truncate">{p.nama}</p>
                <p className="text-sm text-emerald-700 font-medium truncate">{p.jabatan}</p>
                <p className="text-xs text-stone-400 mt-0.5">Urutan #{p.urutan}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  data-testid={`edit-pengurus-${p.id}`}
                  onClick={() => openEdit(p)}
                  className="w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5 text-stone-600" />
                </button>
                <button
                  data-testid={`delete-pengurus-${p.id}`}
                  onClick={() => handleDelete(p.id)}
                  className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Preview hint */}
      {list.length > 0 && (
        <p className="text-xs text-stone-400 text-center mt-4">
          {list.length} pengurus terdaftar — tampil di halaman publik masjid sebagai 2 kolom per baris
        </p>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
                <h3 className="text-xl font-heading font-bold text-primary-dark">
                  {editing ? 'Edit Pengurus' : 'Tambah Pengurus Baru'}
                </h3>
                <button onClick={closeModal} className="p-2 hover:bg-stone-100 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <ImageUpload
                  value={form.foto_url}
                  onChange={(url) => setForm({ ...form, foto_url: url })}
                  label="Foto Pengurus"
                />

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">Nama Lengkap *</label>
                  <input
                    type="text"
                    data-testid="pengurus-nama-input"
                    value={form.nama}
                    onChange={e => setForm({ ...form, nama: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: H. Ahmad Fauzi, S.Ag"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">Jabatan *</label>
                  <input
                    type="text"
                    data-testid="pengurus-jabatan-input"
                    value={form.jabatan}
                    onChange={e => setForm({ ...form, jabatan: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Ketua Takmir, Sekretaris, Bendahara..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Urutan Tampil <span className="text-xs text-stone-400">(angka kecil = tampil lebih awal)</span>
                  </label>
                  <input
                    type="number"
                    data-testid="pengurus-urutan-input"
                    value={form.urutan}
                    onChange={e => setForm({ ...form, urutan: e.target.value })}
                    min={1} max={99}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="1"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors">
                    Batal
                  </button>
                  <button type="submit" disabled={saving} data-testid="pengurus-submit-btn"
                    className="flex-1 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors disabled:opacity-50">
                    {saving ? 'Menyimpan...' : editing ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PengurusManager;
